import {
  PRICE_CURRENCIES,
  PRICE_NAME_MAX,
  PRICE_PRESETS_MAX,
  type PriceForm,
  type PriceIssues,
  minorOf,
} from "../../lib/priceInput";
import { money } from "../../lib/tariff";
import { Field, SelectField } from "../ui/Input";

/**
 * The six fields a price is. pay-ui spec §6.1, §11.1.
 *
 * Shared by `CreatePrice` and `ChangePrice`, which is the whole reason it is a file:
 * the two forms are the same six fields with the same cross-field rules, and the one
 * thing worse than a duplicated form is a duplicated form that drifts — a bound
 * checked when a price is created and not when it is changed is a bound that is not
 * checked.
 *
 * ## Everything here is typed in major units
 *
 * The operator types `0.45`, the server stores `45`. Nothing in this component does
 * that conversion itself — `lib/priceInput.ts` owns it, exactly, in string arithmetic,
 * and is reachable by a test in a way a component is not. What is left here is which
 * fields exist and what they are called.
 *
 * ## The preview line is not decoration
 *
 * A mistyped decimal point is the failure these forms exist to prevent, and it is
 * invisible in a field the operator has just typed into. Rendering the figure back
 * through the same `money()` the fleet table uses is the cheapest way to make
 * `$45.00/kWh` look as wrong as it is.
 *
 * ## What it does not own
 *
 * No `<form>`, no submit button, no prose. Each caller has its own verb, its own
 * warning and its own footer — a change form has to say how many stations it is about
 * to reprice, and a create form has nothing to warn about.
 */

export interface PriceFieldsProps {
  readonly form: PriceForm;
  readonly issues: PriceIssues;
  readonly onChange: (field: keyof PriceForm, value: string) => void;
  /**
   * Whether the currency may still be chosen.
   *
   * Locked when changing a price: the server refuses a currency change on an existing
   * price — every station on it would start asking drivers for a different money — and
   * a field that produces a 400 whatever else the operator does is a field that should
   * not have been editable.
   */
  readonly currencyLocked?: boolean;
  /** Focused on mount. False in a form that opens with a value already in it. */
  readonly autoFocusName?: boolean;
}

export function PriceFields({
  form,
  issues,
  onChange,
  currencyLocked = false,
  autoFocusName = false,
}: PriceFieldsProps) {
  const unit = form.currency === "KHR" ? "1800" : "0.45";

  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2 xl:grid-cols-3">
      <Field
        label="Name"
        value={form.name}
        onChange={(event) => onChange("name", event.target.value)}
        placeholder="Public — daytime"
        hint="What an operator picks it by. Drivers never see it."
        maxLength={PRICE_NAME_MAX}
        required
        autoFocus={autoFocusName}
        {...(issues.name === undefined ? {} : { error: issues.name })}
      />

      <SelectField
        label="Currency"
        value={form.currency}
        onChange={(event) => onChange("currency", event.target.value)}
        hint={
          currencyLocked
            ? "A price keeps the currency it was created in."
            : "Riel has no cents; dollars have two."
        }
        disabled={currencyLocked}
        required
      >
        {PRICE_CURRENCIES.map((code) => (
          <option key={code} value={code}>
            {code}
          </option>
        ))}
      </SelectField>

      <Field
        label="Price per kWh"
        value={form.price}
        onChange={(event) => onChange("price", event.target.value)}
        placeholder={unit}
        inputMode="decimal"
        required
        {...fieldFeedback(issues.price, form.price, form.currency, "per kWh")}
      />

      <Field
        label="Smallest order"
        value={form.minAmount}
        onChange={(event) => onChange("minAmount", event.target.value)}
        placeholder={form.currency === "KHR" ? "4000" : "1.00"}
        inputMode="decimal"
        required
        {...fieldFeedback(issues.minAmount, form.minAmount, form.currency, "minimum")}
      />

      <Field
        label="Largest order"
        value={form.maxAmount}
        onChange={(event) => onChange("maxAmount", event.target.value)}
        placeholder={form.currency === "KHR" ? "200000" : "50.00"}
        inputMode="decimal"
        required
        {...fieldFeedback(issues.maxAmount, form.maxAmount, form.currency, "maximum")}
      />

      <Field
        label="One-tap buttons"
        value={form.presets}
        onChange={(event) => onChange("presets", event.target.value)}
        placeholder={form.currency === "KHR" ? "4000, 20000, 50000" : "2, 5, 10"}
        hint={`Optional, ascending, inside the order range. At most ${PRICE_PRESETS_MAX}.`}
        {...(issues.presets === undefined ? {} : { error: issues.presets })}
      />
    </div>
  );
}

/**
 * A field's error, or — when it is valid — the figure read back as money.
 *
 * One or the other, never both: `Field` shows the hint only in the absence of an
 * error, and the echo is only useful while the number is readable anyway.
 */
function fieldFeedback(
  issue: string | undefined,
  raw: string,
  currency: string,
  suffix: string,
): { error: string } | { hint: string } | Record<string, never> {
  if (issue !== undefined) return { error: issue };

  const minor = minorOf(raw, currency);
  if (minor === undefined) return {};

  return { hint: `${money(minor, currency)} ${suffix}` };
}
